import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { getApiEndpoint } from '@/utils/ERPUtils';
import { logger } from '@/utils/logger';
import {
  streamingMessagesService,
  type StreamingMessageCategory,
  type StreamingMessagesData
} from '@/services/streamingMessagesService';

/** Default interval in milliseconds (3 seconds) */
const DEFAULT_INTERVAL_MS = 3000;

/**
 * Fallback messages used when API call fails. Only the 'General Chat'
 * category is consumed today; the API can still define others and they'll
 * flow through without a fallback.
 */
const FALLBACK_MESSAGES: StreamingMessagesData = {
  'General Chat': [
    'Analyzing your request...',
    'Searching for information...',
    'Gathering relevant data...',
    'Processing your question...',
    'Composing response...',
    'Almost there...',
  ],
};

interface UseStreamingMessagesResult {
  /** All messages grouped by category */
  messages: StreamingMessagesData;
  /** Cycling interval in milliseconds (from Gemini Settings) */
  intervalMs: number;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Get messages for a specific category (with fallback) */
  getMessages: (category: StreamingMessageCategory) => string[];
  /** Refresh messages from API */
  refresh: () => Promise<void>;
}

/**
 * Hook to fetch and cache streaming messages from ERPNext
 *
 * Provides configurable status messages for AI generation, video processing,
 * file uploads, and other async operations. Falls back to hardcoded messages
 * if the API call fails.
 *
 * @example
 * const { getMessages, isLoading } = useStreamingMessages();
 *
 * // Get messages for design generation
 * const designMessages = getMessages('Design Generation');
 *
 * // Use with useMessageCycle hook
 * const generatingMessage = useMessageCycle(isGenerating, designMessages, 3000);
 */
export function useStreamingMessages(): UseStreamingMessagesResult {
  const [messages, setMessages] = useState<StreamingMessagesData>(FALLBACK_MESSAGES);
  const [intervalMs, setIntervalMs] = useState<number>(DEFAULT_INTERVAL_MS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Fetch interval from Gemini Settings
   */
  const fetchInterval = useCallback(async () => {
    try {
      const response = await axios.get(getApiEndpoint('GET_GEMINI_API_HEALTH'), {
        withCredentials: true
      });
      const data = response.data?.message || response.data;
      if (data?.streaming_message_interval) {
        const interval = parseInt(data.streaming_message_interval, 10);
        if (!isNaN(interval) && interval > 0) {
          setIntervalMs(interval);
        }
      }
    } catch (err) {
      logger.warn('[useStreamingMessages] Failed to fetch interval, using default:', err);
      // Keep using default interval
    }
  }, []);

  /**
   * Fetch all messages from API
   */
  const fetchMessages = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await streamingMessagesService.getAllMessages();

      // Merge with fallback to ensure all categories have messages
      const mergedMessages: StreamingMessagesData = { ...FALLBACK_MESSAGES };
      for (const [category, categoryMessages] of Object.entries(data)) {
        if (categoryMessages && categoryMessages.length > 0) {
          mergedMessages[category] = categoryMessages;
        }
      }

      setMessages(mergedMessages);
    } catch (err) {
      logger.warn('[useStreamingMessages] Failed to fetch messages, using fallback:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch messages'));
      // Keep using fallback messages
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Initial fetch on mount
   */
  useEffect(() => {
    fetchMessages();
    fetchInterval();
  }, [fetchMessages, fetchInterval]);

  /**
   * Get messages for a specific category with fallback
   */
  const getMessages = useCallback((category: StreamingMessageCategory): string[] => {
    return messages[category] || FALLBACK_MESSAGES[category] || [];
  }, [messages]);

  /**
   * Refresh messages from API
   */
  const refresh = useCallback(async () => {
    streamingMessagesService.clearCache();
    await fetchMessages();
  }, [fetchMessages]);

  return useMemo(() => ({
    messages,
    intervalMs,
    isLoading,
    error,
    getMessages,
    refresh,
  }), [messages, intervalMs, isLoading, error, getMessages, refresh]);
}

/**
 * Hook to get messages for a specific category
 *
 * @param category - The category to fetch messages for
 * @returns Array of messages for the category
 *
 * @example
 * const videoMessages = useStreamingMessagesByCategory('Video Generation');
 */
export function useStreamingMessagesByCategory(category: StreamingMessageCategory): string[] {
  const { getMessages } = useStreamingMessages();
  return useMemo(() => getMessages(category), [getMessages, category]);
}
